/**@flow */

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import { Field, reduxForm, getFormValues } from "redux-form";
import { compose } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import {
  Form,
  Row,
  Col,
  Label,
  Icon,
  renderTextField,
  renderComboBox,
  Button,
  TooltipDropdown,
  ComboBox,
  View,
  Text,
  Loader,
} from "@components";

import { formatDateTime } from "@transformers/date";
import { DatePickerFormWrapper } from "@components/form";
import { isAfter } from "date-fns";
import LateReasonModal from "../late-reason-modal";
import type { QuestionAnswers, SelectedTask } from "@modules/reporting/reducer/tasks/types";
import type { FormProps } from "redux-form";
import { type ComponentType } from "react";
import type { State } from "@store/reducers";
import actionCreator from "@modules/reporting/actionCreator";
import * as selectors from "@modules/reporting/selectors/tasks";
import { answerTypesEnum } from "@modules/reporting/constants/reports";
import { LabelTemplate } from "@components/schema-form/templates";
import { formatQuestionAnswerResponse } from "@modules/shared/utils/question-answer-response";

type OwnProps = $Exact<{
  clientId: number,
  employeeId?: number,
  onPrint: () => void,
  onSubmit?: () => void,
  printForm: boolean,
  visible?: boolean,
}>;

type PropsFromState = $Exact<{
  data: ?QuestionAnswers,
  values: ?Object,
  selectedTask: ?SelectedTask,
  isLateSubmissionModalVisible: boolean,
  isLoading: boolean,
  isReports?: boolean,
}>;

type PropsFromDispatch = $Exact<{
  saveDraftTask: typeof actionCreator.SaveDraftRTWInterviewTask,
  submitTask: typeof actionCreator.SubmitRTWInterviewTask,
  toggleLateSubmissionModal: typeof actionCreator.ToggleLateSubmissionModal,
}>;

type Props = $Exact<{
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...$Exact<FormProps>, // https://stackoverflow.com/a/44771268/220839
  ...OwnProps,
}>;

export const renderMultiSelect: ComponentType<*> = ({
  label,
  input: { onChange, value },
  ...custom
}) => {
  return (
    <ComboBox
      {...custom}
      id={custom.id || "multi-select"}
      className={custom.className}
      onChange={onChange}
      defaultValue={(value || []).map(item => ({
        item,
      }))}
      value={value}
      isMulti
    />
  );
};

const fieldsEnum = {
  FREE_TEXT: renderTextField,
  DATE_PICK: DatePickerFormWrapper,
  SINGLE_SELECT: renderComboBox,
  MULTI_SELECT: renderMultiSelect,
  CONFIRMATION: "div",
};

type SubmitAction =
  | typeof actionCreator.SubmitRTWInterviewTask
  | typeof actionCreator.SaveDraftRTWInterviewTask;

type CompState = {
  dateTimeInterviewCompletedHasError: boolean,
};
export class RTWInterviewForm extends React.Component<Props, CompState> {
  state = { dateTimeInterviewCompletedHasError: false };
  static defaultProps = {
    onPrint: () => {},
  };

  componentDidMount() {
    this.initForm();
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.props.initialized || (prevProps.submitting && !this.props.submitting)) {
      this.initForm();
    }
  }

  initForm = () => {
    this.props.initialize &&
      this.props.initialize(
        this.data
          .filter(item => item.question.questionType)
          .reduce((acc, current) => {
            return current.answer
              ? {
                  ...acc,
                  [`answer-${current.question.id}`]: current.answer.value,
                }
              : acc;
          }, {}),
      );
  };

  get values() {
    return this.props.values || {};
  }

  formField = (item: Object) => {
    let value = this.values[`answer-${item.question.id}`] || "";
    const type = (item.question || {}).questionType;
    if (type === "DATE_PICK") {
      value = formatDateTime(value);
    } else if (type === "MULTI_SELECT" && value) {
      value = value.join(",");
    } else if (type === "CONFIRMATION") value = "";

    const hasAnswer = !!((this.values || {})[`answer-${item.question.id}`] || []).length;

    return (
      <View
        name="rtw-interview-form"
        flex="1"
        alignItems="baseline"
        gap={3}
        direction="row"
        key={item.question.id}
        className="d-print-none"
      >
        {item.question.questionType && item.question.questionType !== "CONFIRMATION" ? (
          <View direction="row" gap={3} flex="1">
            <View flex="1" gap={3} direction="row" justifyContent="space-between">
              <LabelTemplate id={item.question.questionText} label={item.question.questionText} />
              <View justifyContent="center" alignItems="center">
                {item.question.isMandatory && <Text size={1} color="pumpkin-orange" text="*" />}
              </View>
            </View>

            <View flex="1">
              <Text className="d-print-only" text={value} />
              <Field
                name={`answer-${item.question.id}`}
                className={`d-print-none`}
                component={fieldsEnum[item.question.questionType]}
                options={item.question.selectableValues.map(value => ({
                  value,
                  label: value,
                }))}
                items={[
                  {
                    value: true,
                    label: this.props.t("YES"),
                    active: (this.values || {})[`answer-${item.question.id}`] === true,
                  },
                  {
                    value: false,
                    label: this.props.t("NO"),
                    active: (this.values || {})[`answer-${item.question.id}`] === false,
                  },
                ]}
                type="textarea"
              />
            </View>
          </View>
        ) : (
          <Label className="small-font-size">{item.question.questionText}</Label>
        )}
      </View>
    );
  };

  get isValid() {
    return (
      [
        ...this.data
          .filter(item => item.question.isMandatory)
          .map(item => `answer-${item.question.id}`),
        "completionDate",
      ].every(item => this.values && this.values[item]) &&
      !this.state.dateTimeInterviewCompletedHasError
    );
  }

  get dataToSubmit() {
    const questionAnswers = this.data
      .filter(item => this.values.hasOwnProperty(`answer-${item.question.id}`))
      .map(item => ({
        question: { ...item.question },
        answer: {
          value: this.values[`answer-${item.question.id}`],
          type: answerTypesEnum[item.question.questionType],
        },
      }));

    return {
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      taskId: this.task?.id,
      task: {
        ...this.task,
        questionAnswers,
        completionDate: this.values.completionDate,
      },
    };
  }

  handleSubmit = (submitAction: SubmitAction, isSubmit?: boolean) => () => {
    if (!this.values) {
      return;
    }

    if (
      this.task?.lateReasonCapture &&
      isSubmit &&
      !isAfter(this.task?.dueDate, this.values.completionDate)
    ) {
      this.props.toggleLateSubmissionModal(true);
    } else {
      submitAction({
        ...this.dataToSubmit,
        isReports: this.props.isReports,
      });
    }
  };

  onToggleLateReasonModal = (show: boolean) => () => {
    this.props.toggleLateSubmissionModal(show);
  };

  get exportAnswerQuestionTable() {
    return (
      <View className="d-print-only" flex="1">
        <table border="1" cellSpacing={0} cellPadding={3} className="rtw-interview-form-export">
          <thead>
            <tr>
              <th>{this.props.t("QUESTION")}</th>
              <th>{this.props.t("ANSWER")}</th>
            </tr>
          </thead>
          <tbody>
            {this.data.map((qaItem, index) => {
              return (
                <tr key={`qa-key-${index}`}>
                  <td align="left">
                    <div className="ellipsis">{qaItem?.question?.questionText}</div>
                  </td>
                  <td align="left">
                    <div className="ellipsis">
                      {formatQuestionAnswerResponse(
                        {
                          value: this.values[`answer-${qaItem?.question?.id}`],
                          type: qaItem?.question?.questionType,
                        },
                        this.props.t,
                        true,
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </View>
    );
  }
  onError = (err: string) => {
    this.setState({ ...this.state, dateTimeInterviewCompletedHasError: !!err });
  };

  get data() {
    return this.props.questionsData || this.props.data;
  }

  get task() {
    return this.props.task || this.props.selectedTask;
  }

  render() {
    return (
      <View>
        <Form onSubmit={this.props.handleSubmit(this.handleSubmit)}>
          <View gap={3}>
            <View gap={3}>
              {(this.props.initialized || this.props.visible) &&
                !this.props.submitting &&
                this.data.map(this.formField)}
              <View
                name={"DATE_INTERVIEW_COMPLETED"}
                direction="row"
                gap={3}
                flex="1"
                className={`${this.task?.completionDate ? "" : "d-print-none"}`}
              >
                <View direction="row" flex="1">
                  <View flex="1" gap={3} direction="row" justifyContent="space-between">
                    <TooltipDropdown
                      description={this.props.t("RTW_DATE_COMPLETE_TOOLTIP")}
                      id="rtw-interview-date-completed-tooltip"
                    >
                      <LabelTemplate
                        id={this.props.t("DATE_INTERVIEW_COMPLETED")}
                        label={this.props.t("DATE_INTERVIEW_COMPLETED")}
                      />
                    </TooltipDropdown>
                    <View justifyContent="center" alignItems="center">
                      {<Text size={1} color="pumpkin-orange" text="*" />}
                    </View>
                  </View>
                </View>
                <View flex="1">
                  <Text className="d-print-only" text={formatDateTime(this.task?.completionDate)} />
                  <Field
                    name="completionDate"
                    className={`d-print-none`}
                    component={DatePickerFormWrapper}
                    customProps={{
                      onError: this.onError,
                      dateFormat: "DD/MM/YYYY HH:mm",
                      placeholder: "dd/mm/yyyy hh:mm",
                      enableTime: true,
                      minDate: moment(this.task?.rtwDate)
                        .subtract(1, "days")
                        .format("DD/MM/YYYY HH:mm"),
                      value: this.task?.completionDate,
                      drops: "up",
                    }}
                  />
                </View>
              </View>

              {this.exportAnswerQuestionTable}
            </View>
            <View direction="row" className="d-print-none" justifyContent="space-between">
              <View flex={1} direction="row">
                {this.props.printForm && (
                  <Button onClick={this.props.onPrint}>{this.props.t("PRINT_FORM")}</Button>
                )}
              </View>
              <View gap={3} direction="row" justifyContent="start">
                <Button
                  materialIcon="save_as"
                  disabled={this.props.isLoading || this.state.dateTimeInterviewCompletedHasError}
                  onClick={this.handleSubmit(this.props.saveDraftTask)}
                >
                  {this.props.t("TASKS_DETAILS_SAVE_DRAFT")}
                </Button>
                <Button
                  materialIcon="replay_circle_filled"
                  disabled={!this.isValid || this.props.isLoading}
                  onClick={this.handleSubmit(this.props.submitTask, true)}
                  type="submit"
                >
                  {this.props.t("SUBMIT")}
                </Button>
              </View>
            </View>
          </View>
        </Form>
        {this.props.isLateSubmissionModalVisible && (
          <LateReasonModal
            onSubmit={this.props.submitTask}
            onDismissHandler={this.onToggleLateReasonModal(false)}
            dataToSubmit={{
              ...this.dataToSubmit,
              isReports: this.props.isReports,
            }}
            selectedTask={this.task}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  values: getFormValues("rtw-interview")(state),
  data: selectors.getSelectedTaskQuestionAnswers(state),
  selectedTask: selectors.getSelectedTask(state),
  isLateSubmissionModalVisible: selectors.isLateSubmissionModalVisible(state),
  isLoading: selectors.isLoading(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  saveDraftTask: actionCreator.SaveDraftRTWInterviewTask,
  submitTask: actionCreator.SubmitRTWInterviewTask,
  toggleLateSubmissionModal: actionCreator.ToggleLateSubmissionModal,
};

export default compose(
  localize(),
  reduxForm({
    form: "rtw-interview",
    enableReinitialize: true,
  }),
  connect(mapStateToProps, mapDispatchToProps),
)(RTWInterviewForm);
